import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';

/**
 * Closed, outward-facing solids and double-sided cloth for ship templates. Every template is
 * built in its own local frame, so all points are local and the frame is the identity.
 */

export type Ring3 = readonly Vec3[];

export interface SparSpec {
  readonly from: Vec3;
  readonly to: Vec3;
  readonly radiusFrom: number;
  readonly radiusTo: number;
  readonly sides: number;
  readonly painterWidthScale: number;
}

const LOCAL: Frame = { origin: [0, 0, 0], yaw: 0 };
const SAME_POINT_M = 1e-6;
const HORIZONTAL_NORMAL_Y = 0.7;

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function scale(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function length(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}

function samePoint(a: Vec3, b: Vec3): boolean {
  return length(sub(a, b)) < SAME_POINT_M;
}

function newellNormal(points: readonly Vec3[]): Vec3 {
  return points.reduce<Vec3>(
    (n, a, i) => {
      const b = points[(i + 1) % points.length]!;
      return [
        n[0] + (a[1] - b[1]) * (a[2] + b[2]),
        n[1] + (a[2] - b[2]) * (a[0] + b[0]),
        n[2] + (a[0] - b[0]) * (a[1] + b[1]),
      ];
    },
    [0, 0, 0]
  );
}

/* Plank grooves run along a face: horizontal strakes on sides and ends, fore-aft on decks. */
function plankUv(normal: Vec3): (p: Vec3) => Vec2 {
  const n = length(normal);
  if (Math.abs(normal[1]) > HORIZONTAL_NORMAL_Y * n) return (p) => [p[0], p[2]];
  if (Math.abs(normal[0]) > HORIZONTAL_NORMAL_Y * n) return (p) => [p[2], p[1]];
  return (p) => [p[0], p[1]];
}

/* A polygon of more than four corners is fanned from its centroid, not from a corner, so a cap
   whose outline bends out a little past one corner (a transom above its tuck) stays facing out. */
function fanFromCentre(
  builder: MeshBuilder,
  style: VertexStyle,
  corners: readonly Vec3[],
  uvOf: (p: Vec3) => Vec2
): void {
  const c = scale(
    corners.reduce<Vec3>((sum, p) => add(sum, p), [0, 0, 0]),
    1 / corners.length
  );
  corners.forEach((p, i) => {
    builder.polygon(style, LOCAL, [c, p, corners[(i + 1) % corners.length]!], uvOf);
  });
}

/* A patterned face takes the coordinates its caller gives each corner (STYLE 15.1). */
function givenUv(corners: readonly Vec3[], uvs: readonly Vec2[]): (p: Vec3) => Vec2 {
  return (p) => uvs[corners.indexOf(p)]!;
}

/* A face with repeated corners loses them: a quad against a collapsed ring edge is a triangle. */
function emitFace(
  builder: MeshBuilder,
  style: VertexStyle,
  corners: readonly Vec3[],
  uvs?: readonly Vec2[]
): void {
  const distinct = corners.filter((p, i) => !samePoint(p, corners[(i + 1) % corners.length]!));
  if (distinct.length < 3) return;
  const uvOf = uvs ? givenUv(corners, uvs) : plankUv(newellNormal(distinct));
  if (distinct.length === 4) {
    const [a, b, c, d] = distinct as [Vec3, Vec3, Vec3, Vec3];
    builder.quad(style, LOCAL, [a, b, c, d], [uvOf(a), uvOf(b), uvOf(c), uvOf(d)]);
  } else if (distinct.length === 3) {
    builder.polygon(style, LOCAL, distinct, uvOf);
  } else {
    fanFromCentre(builder, style, distinct, uvOf);
  }
}

function isCollapsed(ring: Ring3): boolean {
  return length(newellNormal(ring)) < SAME_POINT_M;
}

/**
 * A closed solid through convex rings stacked along a sweep (a hull's +X, a palisade's path).
 * Each ring runs so that the sweep direction crossed with its tangent points out of the solid,
 * which makes the ring face back along the sweep as a polygon: the first ring caps the solid as
 * given, the last one reversed. A ring collapsed to a line (a stem or sternpost) closes the
 * solid by itself and gets no cap. `sideStyle` styles the strip swept by ring edge k, from
 * point k to the next; the caps and the unstyled strips take `style`. A patterned strip takes
 * `sideUv` of ring r's point k at each corner; the rest keep their plank coordinates.
 */
export function loftSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  rings: readonly Ring3[],
  sideStyle?: (k: number) => VertexStyle,
  sideUv?: (r: number, k: number) => Vec2
): void {
  for (let r = 0; r + 1 < rings.length; r++) {
    const [a, b] = [rings[r]!, rings[r + 1]!];
    for (let k = 0; k < a.length; k++) {
      const next = (k + 1) % a.length;
      const strip = sideStyle?.(k) ?? style;
      const uvs =
        strip.pattern && sideUv
          ? [sideUv(r, k), sideUv(r + 1, k), sideUv(r + 1, next), sideUv(r, next)]
          : undefined;
      emitFace(builder, strip, [a[k]!, b[k]!, b[next]!, a[next]!], uvs);
    }
  }
  const first = rings[0]!;
  const last = rings[rings.length - 1]!;
  if (!isCollapsed(first)) emitFace(builder, style, first);
  if (!isCollapsed(last)) emitFace(builder, style, [...last].reverse());
}

function sparFrame(axis: Vec3): readonly [Vec3, Vec3] {
  const helper: Vec3 = Math.abs(axis[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = cross(axis, helper);
  const unitU = scale(u, 1 / length(u));
  return [unitU, cross(axis, unitU)];
}

function validateSpar(spec: SparSpec): number {
  const span = length(sub(spec.to, spec.from));
  if (!(span > SAME_POINT_M)) throw new Error(`Spar needs a positive length, got ${span}`);
  if (!(spec.radiusFrom > 0 && spec.radiusTo > 0)) {
    throw new Error(
      `Spar needs a positive radius at both ends, got ${spec.radiusFrom}, ${spec.radiusTo}`
    );
  }
  if (spec.sides < 3) throw new Error(`Spar needs at least 3 sides, got ${spec.sides}`);
  return span;
}

/**
 * A closed n-sided frustum from `from` to `to`; radii are circumradii, as in MeshBuilder.cylinder.
 * Side faces come first, in ring order, then the end caps. For the painter view each side face
 * carries its own outward offset, so its plane moves to `painterWidthScale` times its distance
 * from the axis: the builder styles whole faces, and at painter distance the seams this opens
 * along the edges are far below a pixel.
 */
export function sparSolid(builder: MeshBuilder, style: VertexStyle, spec: SparSpec): void {
  const span = validateSpar(spec);
  const axis = scale(sub(spec.to, spec.from), 1 / span);
  const [u, v] = sparFrame(axis);
  const step = (2 * Math.PI) / spec.sides;
  const radial = (angle: number): Vec3 => add(scale(u, Math.cos(angle)), scale(v, Math.sin(angle)));
  const ring = (centre: Vec3, r: number): Vec3[] =>
    Array.from({ length: spec.sides }, (_, k) => add(centre, scale(radial((k + 0.5) * step), r)));
  const base = ring(spec.from, spec.radiusFrom);
  const top = ring(spec.to, spec.radiusTo);
  const apothem = ((spec.radiusFrom + spec.radiusTo) / 2) * Math.cos(step / 2);
  const arc = 2 * apothem * Math.tan(step / 2);
  for (let k = 0; k < spec.sides; k++) {
    const next = (k + 1) % spec.sides;
    const widen = scale(radial((k + 1) * step), apothem * (spec.painterWidthScale - 1));
    const faceStyle = spec.painterWidthScale === 1 ? style : { ...style, axisOffset: widen };
    const uv: readonly [Vec2, Vec2, Vec2, Vec2] = [
      [0, k * arc],
      [0, (k + 1) * arc],
      [span, (k + 1) * arc],
      [span, k * arc],
    ];
    builder.quad(faceStyle, LOCAL, [base[k]!, base[next]!, top[next]!, top[k]!], uv);
  }
  builder.polygon(style, LOCAL, top);
  builder.polygon(style, LOCAL, [...base].reverse());
}

/* One cloth cell, front and back; against a collapsed edge (a pennant's tip) it is a triangle. */
function clothCell(
  builder: MeshBuilder,
  style: VertexStyle,
  corners: readonly Vec3[],
  uvs?: readonly Vec2[]
): void {
  const distinct = corners.filter((p, i) => !samePoint(p, corners[(i + 1) % corners.length]!));
  const uvOf = uvs ? givenUv(corners, uvs) : undefined;
  if (distinct.length === 4) {
    const [a, b, d, e] = distinct as [Vec3, Vec3, Vec3, Vec3];
    const uv = (points: readonly [Vec3, Vec3, Vec3, Vec3]) =>
      uvOf ? (points.map(uvOf) as unknown as readonly [Vec2, Vec2, Vec2, Vec2]) : undefined;
    builder.quad(style, LOCAL, [a, b, d, e], uv([a, b, d, e]));
    builder.quad(style, LOCAL, [a, e, d, b], uv([a, e, d, b]));
  } else if (distinct.length === 3) {
    builder.polygon(style, LOCAL, distinct, uvOf);
    builder.polygon(style, LOCAL, [...distinct].reverse(), uvOf);
  }
}

/* Metres along each row from its first column and down each column from its first row, where a
   sail's cloths run from its luff and its bonnet hangs from its head (STYLE 15.1). */
function clothCoords(grid: readonly (readonly Vec3[])[]): Vec2[][] {
  const apart = (p: Vec3, q: Vec3): number => length(sub(p, q));
  const alongRow = grid.map((row) =>
    row.reduce<number[]>(
      (sums, p, c) => [...sums, c === 0 ? 0 : sums[c - 1]! + apart(p, row[c - 1]!)],
      []
    )
  );
  const downColumn = grid.reduce<number[][]>(
    (sums, row, r) => [
      ...sums,
      row.map((p, c) => (r === 0 ? 0 : sums[r - 1]![c]! + apart(p, grid[r - 1]![c]!))),
    ],
    []
  );
  return grid.map((row, r) => row.map((_, c): Vec2 => [alongRow[r]![c]!, downColumn[r]![c]!]));
}

/**
 * Cloth is a membrane, not a solid: each cell is emitted once per side, the back as the exact
 * reverse of the front (same diagonal), so either side is front-facing and the pair bounds no
 * volume. `grid` is rows of points; every row has the same length. A patterned cloth lies in
 * metres along its rows and down its columns.
 */
export function clothMembrane(
  builder: MeshBuilder,
  style: VertexStyle,
  grid: readonly (readonly Vec3[])[]
): void {
  const coords = style.pattern ? clothCoords(grid) : undefined;
  for (let r = 0; r + 1 < grid.length; r++) {
    const [upper, lower] = [grid[r]!, grid[r + 1]!];
    for (let c = 0; c + 1 < upper.length; c++) {
      const uvs = coords && [
        coords[r]![c]!,
        coords[r]![c + 1]!,
        coords[r + 1]![c + 1]!,
        coords[r + 1]![c]!,
      ];
      clothCell(builder, style, [upper[c]!, upper[c + 1]!, lower[c + 1]!, lower[c]!], uvs);
    }
  }
}
